import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { getImagePath } from '../utils/imageUtils';

const stats = [
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
    title: 'Olympiad Medals',
    value: '5',
    description: 'Students who have won medals in various Olympiads',
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    title: 'Expert Trainers',
    value: '15+',
    description: 'Experienced Olympiad trainers and mentors',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    title: 'Successful Students',
    value: '20+',
    description: 'Students trained for Olympiad competitions',
  },
  {
    icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
    title: 'Years of Excellence',
    value: '1+',
    description: 'Years of Olympiad training experience',
  },
];

const team = [
  {
    name: 'Mrs. Nirmala Singh',
    role: 'Head of Mathematics Olympiad',
    image: getImagePath('team-1.jpg'),
    bio: 'Expertise in Mathematics Olympiad training. 15+ years of Olympiad training experience. Deep interest in teaching Mathematics Olympiad.',
  },
  {
    name: 'Mr. D.P. Thakur',
    role: 'Head of Science Olympiad',
    image: getImagePath('team-2.jpg'),
    bio: 'Specialization in Olympiad training. Mentored 100+ ISO aspirants. Deep knowledge of Olympiad syllabus.',
  },
  {
    name: 'Mrs. Archana Kumari',
    role: 'Head of English Olympiad',
    image: getImagePath('team-3.jpg'),
    bio: 'Expertise in IEO preparation. 12+ years of experience in Olympiad training. Strong command over English Olympiad syllabus.',
  },
];

const About = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" align="center" gutterBottom>
            About Junior Olympiad
          </Typography>
          <Typography variant="h6" align="center" sx={{ maxWidth: 800, mx: 'auto' }}>
            Leading the way in Olympiad preparation with proven track record and expert trainers
          </Typography>
        </Container>
      </Box>

      {/* Story Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Our Journey in Olympiad Excellence
            </Typography>
            <Typography variant="body1" paragraph>
              Founded in 2023, Junior Olympiad has been at the forefront of Olympiad preparation in India.
              Our mission is to nurture young talents and prepare them for national and international
              Olympiad competitions.
            </Typography>
            <Typography variant="body1" paragraph>
              We believe that every student has the potential to excel in Olympiads with the right
              guidance and structured preparation. Our specialized training programs for IMO, ISO, IEO, ISSO and
              IHO will help students achieve their dreams of winning medals in these
              prestigious competitions.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImagePath('about-hero.jpg')}
              alt="Students celebrating Olympiad success"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Stats Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>{stat.icon}</Box>
                    <Typography variant="h4" component="h3" gutterBottom>
                      {stat.value}
                    </Typography>
                    <Typography variant="h6" component="h4" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission and Vision */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Our Mission
                </Typography>
                <Typography variant="body1" paragraph>
                  To provide world-class Olympiad preparation through expert guidance, comprehensive
                  study material, and personalized attention, enabling students to achieve excellence
                  in national and international Olympiad competitions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Our Vision
                </Typography>
                <Typography variant="body1" paragraph>
                  To be the leading Olympiad training institute in India, recognized for producing
                  the highest number of medalists in various Olympiad competitions while maintaining
                  the highest standards of education and student development.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Team Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Meet Our Olympiad Experts
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
            Learn from the best in Olympiad training
          </Typography>

          <Grid container spacing={4} sx={{ mt: 4 }}>
            {team.map((member, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Avatar
                      src={member.image}
                      alt={member.name}
                      sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" component="h3" gutterBottom>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {member.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 